<script setup lang="ts">
/**
 * _RobleyShortcuts — global keyboard shortcut handler for /robley/* routes.
 *
 * Mounted once in App.vue. Self-guards to /robley/* via route.path check so
 * it is a no-op on every other route. Keys are suppressed while the user is
 * typing in an input/textarea/select/contenteditable.
 *
 * UX-28
 */
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const showCheatSheet = ref(false)

const SHORTCUTS = [
  { key: 'n', label: 'New campaign',          path: '/robley/nurture/new' },
  { key: 'l', label: 'Campaigns list',         path: '/robley/nurture' },
  { key: 'c', label: 'Calendar',               path: '/robley/nurture/calendar' },
  { key: 't', label: 'Templates',              path: '/robley/nurture/templates' },
  { key: 'h', label: 'Hot Leads',              path: '/robley/nurture/hot-leads' },
  { key: 'a', label: 'Audit / Notes feed',     path: '/robley/nurture/notes-feed' },
  { key: 'p', label: 'Submission Pickups',     path: '/robley/pickups' },
  { key: '?', label: 'Show this cheat sheet',  action: 'cheat-sheet' },
] as const

type Shortcut = typeof SHORTCUTS[number]

function isEditable(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  const tag = el.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (el.isContentEditable) return true
  return false
}

function handleKeydown(e: KeyboardEvent) {
  // Only active on /robley/* routes
  if (!route.path.startsWith('/robley/')) return
  // Skip typing contexts
  if (isEditable(document.activeElement)) return
  // Skip OS/app modifier combos (Ctrl+C, Cmd+N, etc.)
  if (e.ctrlKey || e.metaKey || e.altKey) return

  // Normalise '?' — some keyboards send Shift+/ rather than literal '?'
  const k = (e.key === '/' && e.shiftKey) ? '?' : e.key.toLowerCase()

  const sc = SHORTCUTS.find((s) => s.key === k) as Shortcut | undefined
  if (!sc) return

  e.preventDefault()

  if ('action' in sc && sc.action === 'cheat-sheet') {
    showCheatSheet.value = true
  } else if ('path' in sc) {
    router.push(sc.path)
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <PDialog
    v-model:visible="showCheatSheet"
    :modal="true"
    :closable="true"
    :draggable="false"
    style="width: 480px"
    header="Keyboard Shortcuts"
  >
    <div>
      <p class="text-sm text-secondary-500 mb-4">
        Press these keys anywhere on the Robley pages to navigate quickly.
        Shortcuts pause while you're typing in a form.
      </p>
      <div
        v-for="sc in SHORTCUTS"
        :key="sc.key"
        class="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0"
      >
        <span class="text-sm text-secondary-700">{{ sc.label }}</span>
        <kbd
          class="inline-flex items-center justify-center min-w-7 h-7 px-2 text-xs font-semibold rounded border border-zinc-300 bg-zinc-50 text-zinc-700 shadow-sm font-mono"
        >{{ sc.key }}</kbd>
      </div>
    </div>
  </PDialog>
</template>
